import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useDebouncedEffect } from 'src/hooks/use-debounced-effect';
import useOnClickOutside from 'src/hooks/use-on-click-outside';
import { useWindowListener } from 'src/hooks/use-window-listener';
import { Typography } from 'src/ui/common/atoms/typography';
import { TextInput } from '../../atoms/text-input';
import { DropDown } from '../dropdown';
import { ISelect, OptionT } from './select.types';
import {
    StyledArrow,
    StyledContent,
    StyledHeader,
    StyledInputWrapper,
    StyledOption,
    StyledSearchContent,
    StyledSelectedOption,
    StyledTypographyError,
} from './styles';

export const Select = ({
    options: initialOptions,
    defaultValue,
    value,
    className,
    testId,
    onChange,
    onSearch,
    fitParentWidth,
    placeholder,
    searchPlaceholder,
    error,
    minQueryLength = 2,
    emptyIcon,
    inViewOptions,
    setInputElement,
}: ISelect) => {
    const { ref: setInViewRef, inView } = useInView(inViewOptions);
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchValue, setSearchValue] = useState('');
    const [contentMaxHeight, setContentMaxHeight] = useState<number | undefined>();
    const [opened, setOpened] = useState(false);
    const [options, setOptions] = useState(initialOptions);
    const [selected, setSelected] = useState<OptionT | undefined>(value ?? defaultValue);

    const toggle = useCallback(() => {
        setOpened((s) => !s);
    }, []);

    const close = useCallback(() => {
        setOpened(false);
    }, []);

    useWindowListener('orientationchange', close);

    const handleOutsideClick = useCallback(
        ({ target }) => {
            if (target.closest?.('[data-element="dropdown"]')) {
                return;
            }
            setTimeout(close, 0);
        },
        [close]
    );

    const handleContentClick = useCallback(
        ({ target }) => {
            setTimeout(close, 0);
        },
        [close]
    );

    const getOptions = useCallback(async () => {
        if (!onSearch) {
            setOptions(initialOptions);
            return;
        }
        if (!searchValue || searchValue.length >= minQueryLength) {
            const options = await onSearch(searchValue);
            setOptions(options);
        }
    }, [minQueryLength, onSearch, searchValue, initialOptions]);

    const handleInputChange = useCallback(({ target }: React.ChangeEvent<HTMLInputElement>) => {
        const query = target.value;
        setSearchValue(query);
    }, []);

    const calculateContentMaxHeight = useCallback(() => {
        if (dropdownRef.current) {
            const { top } = dropdownRef.current?.getBoundingClientRect();
            const { innerHeight } = window;
            setContentMaxHeight(innerHeight - top);
        }
    }, []);

    const handleSelect = useCallback(
        (option: OptionT) => {
            if (selected?.value === option.value) {
                return;
            }
            setSelected(option);
            onChange?.(option);
        },
        [onChange, selected]
    );

    useOnClickOutside(dropdownRef, handleOutsideClick, triggerRef);

    const showTextInput = useMemo(() => typeof selected?.label === 'string' || !selected?.label, [selected]);

    useDebouncedEffect(() => setSelected(value), 0, [value]);
    useDebouncedEffect(getOptions, 0, [searchValue, initialOptions]);

    useEffect(() => {
        if (opened) {
            calculateContentMaxHeight();
            setTimeout(calculateContentMaxHeight, 300);
        }
    }, [calculateContentMaxHeight, opened]);

    useEffect(() => {
        inputRef.current && setInputElement?.(inputRef.current);
    }, [inputRef, setInputElement]);

    useEffect(() => {
        triggerRef.current && setInViewRef(triggerRef.current);
    }, [triggerRef, setInViewRef]);

    useEffect(() => {
        !inView && close();
    }, [inView, close]);

    return (
        <>
            <StyledHeader ref={triggerRef} onClick={toggle} data-test-id={testId} className={className}>
                {showTextInput ? (
                    <StyledInputWrapper flexDirection="column" emptyIcon={!!emptyIcon && !selected?.label}>
                        <TextInput
                            ref={inputRef}
                            onChange={handleInputChange}
                            value={selected?.label as string}
                            data-test-id={`${testId}-input`}
                            placeholder={placeholder}
                        />
                        {!selected?.label && emptyIcon}
                    </StyledInputWrapper>
                ) : (
                    <StyledSelectedOption opened={opened} justifyContent="flex-start" alignItems="center">
                        {selected?.label}
                    </StyledSelectedOption>
                )}
                <StyledArrow opened={opened} icon="chevronDown" variant="regular" />
            </StyledHeader>
            {!!error && (
                <StyledTypographyError color="cyan500" size="xs">
                    {error}
                </StyledTypographyError>
            )}
            <DropDown
                opened={opened}
                nodeRef={triggerRef}
                dropdownRef={dropdownRef}
                horizontalBindTo="right"
                fitParentWidth={fitParentWidth}
            >
                <DropDown.Content>
                    {onSearch && (
                        <StyledSearchContent>
                            <TextInput
                                stretch
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={handleInputChange}
                            />
                        </StyledSearchContent>
                    )}
                    <StyledContent
                        withSearch={!!onSearch}
                        data-test-id={`${testId}-dropdown`}
                        onClick={handleContentClick}
                        style={{ maxHeight: `${contentMaxHeight}px` }}
                    >
                        {options?.map((option) => (
                            <StyledOption
                                onClick={() => handleSelect(option)}
                                key={option.value}
                                selected={selected?.value === option.value}
                                data-component="select"
                            >
                                <Typography altTitle={typeof option.label === 'string' ? option.label : undefined}>
                                    {option.label}
                                </Typography>
                            </StyledOption>
                        ))}
                    </StyledContent>
                </DropDown.Content>
            </DropDown>
        </>
    );
};